import React, { useState, useEffect, useRef, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./individualProperty.css";

const placeholderImage = "/images/imagePlaceholder.webp";

// Custom hook for responsive thumbnails
const useResponsiveThumbnails = () => {
  const [visibleThumbnails, setVisibleThumbnails] = useState(3);

  useEffect(() => {
    const updateVisibleThumbnails = () => {
      const width = window.innerWidth;
      if (width >= 1400) {
        setVisibleThumbnails(8);
      } else if (width >= 1200) {
        setVisibleThumbnails(7);
      } else if (width >= 1024) {
        setVisibleThumbnails(6);
      } else if (width >= 768) {
        setVisibleThumbnails(5);
      } else if (width >= 640) {
        setVisibleThumbnails(4);
      } else if (width >= 480) {
        setVisibleThumbnails(3);
      } else {
        setVisibleThumbnails(2);
      }
    };

    updateVisibleThumbnails();
    window.addEventListener("resize", updateVisibleThumbnails);
    return () => window.removeEventListener("resize", updateVisibleThumbnails);
  }, []);

  return visibleThumbnails;
};

function Gallery({ images = [], video }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(
    Array(images.length).fill(false)
  );
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [mainImageLoaded, setMainImageLoaded] = useState(Array(images.length).fill(false));
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const sliderRef = useRef(null);
  const lightboxSliderRef = useRef(null);
  const thumbnailRefs = useRef([]);
  
  const visibleThumbnails = useResponsiveThumbnails();

  // Original teal theme colors
  const themeColors = {
    primary: "#10c9c0",
    hover: "#0ea5a0",
    light: "#10c9c020",
    shadow: "#10c9c0"
  };

  // Open lightbox when clicking on main image
  const openLightbox = useCallback((index = currentImageIndex) => {
    setLightboxIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  }, [currentImageIndex]);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setShowLightbox(false);
    document.body.style.overflow = 'unset'; // Restore scroll
  }, []);

  // Keyboard navigation for both gallery and lightbox
  useEffect(() => {
    if (images.length === 0) return;

    const handleKeyDown = (e) => {
      if (showLightbox) {
        if (e.key === "Escape") {
          e.preventDefault();
          closeLightbox();
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          lightboxSliderRef.current?.slickPrev();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          lightboxSliderRef.current?.slickNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, showLightbox, closeLightbox]);

  const changeImage = useCallback((index) => {
    if (index === currentImageIndex) return;
    
    setCurrentImageIndex(index);
    // Only show loading if the image hasn't been loaded yet
    if (!mainImageLoaded[index]) {
      setIsImageLoading(true);
    } else {
      setIsImageLoading(false);
    }
    sliderRef.current?.slickGoTo(index);
  }, [currentImageIndex, mainImageLoaded]);

  const handleThumbnailLoad = useCallback((index) => {
    setThumbnailLoaded((loaded) =>
      loaded.map((value, i) => (i === index ? true : value))
    );
  }, []);

  const handleMainImageLoad = useCallback((index) => {
    setMainImageLoaded((loaded) =>
      loaded.map((value, i) => (i === index ? true : value))
    );
    // Only set loading to false if it's the current image
    if (index === currentImageIndex) {
      setIsImageLoading(false);
    }
  }, [currentImageIndex]);

  // Main slider settings
  const mainSliderSettings = {
    infinite: images.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    swipeToSlide: true,
    touchThreshold: 5, // More sensitive on mobile
    adaptiveHeight: true,
    lazyLoad: 'ondemand',
    beforeChange: (current, next) => {
      setCurrentImageIndex(next);
      // Only show loading if the image hasn't been loaded yet
      if (!mainImageLoaded[next]) {
        setIsImageLoading(true);
      } else {
        setIsImageLoading(false);
      }
    },
  };

  // Lightbox slider settings
  const lightboxSliderSettings = {
    infinite: images.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    swipeToSlide: true,
    touchThreshold: 5,
    initialSlide: lightboxIndex,
    beforeChange: (current, next) => {
      setLightboxIndex(next);
    },
  };

  // Return early if no images
  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-64 bg-gray-100 rounded-xl">
        <p className="text-gray-500">No images available</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full px-2 sm:px-4 md:px-0 md:w-11/12 lg:w-10/12 xl:w-9/12 mx-auto mt-4 sm:mt-6 md:mt-8">
        {/* Main Image Slider */}
        <div className="relative w-full group">
          {/* Loading overlay */}
          {isImageLoading && !mainImageLoaded[currentImageIndex] && (
            <div className="absolute inset-0 bg-gray-50 animate-pulse rounded-xl md:rounded-2xl z-10 flex items-center justify-center">
              <div 
                className="w-6 h-6 sm:w-8 sm:h-8 border-2 sm:border-4 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: `${themeColors.primary} transparent transparent transparent` }}
              ></div>
            </div>
          )}

          {/* Desktop navigation arrows only */}
          {images.length > 1 && (
            <>
              <button
                className="hidden lg:flex absolute top-1/2 transform -translate-y-1/2 left-[-2.5rem] xl:left-[-2rem] 
                           items-center justify-center w-10 h-10 xl:w-12 xl:h-12 text-white opacity-0 group-hover:opacity-80 
                           rounded-full hover:opacity-100 hover:scale-110 hover:-translate-x-1 
                           transition-all duration-300 z-20 shadow-lg
                           focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  backgroundColor: themeColors.primary,
                  boxShadow: `0 4px 14px 0 ${themeColors.light}`
                }}
                onClick={() => sliderRef.current?.slickPrev()}
                aria-label="Previous image"
              >
                <svg className="w-5 h-5 xl:w-6 xl:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                className="hidden lg:flex absolute top-1/2 transform -translate-y-1/2 right-[-2.5rem] xl:right-[-2rem] 
                           items-center justify-center w-10 h-10 xl:w-12 xl:h-12 text-white opacity-0 group-hover:opacity-80 
                           rounded-full hover:opacity-100 hover:scale-110 hover:translate-x-1 
                           transition-all duration-300 z-20 shadow-lg
                           focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  backgroundColor: themeColors.primary,
                  boxShadow: `0 4px 14px 0 ${themeColors.light}`
                }}
                onClick={() => sliderRef.current?.slickNext()}
                aria-label="Next image"
              >
                <svg className="w-5 h-5 xl:w-6 xl:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image counter - responsive sizing */}
          <div 
            className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm z-20 shadow-md"
            style={{ backgroundColor: `${themeColors.primary}CC` }}
          >
            {currentImageIndex + 1} / {images.length}
          </div>

          {/* Expand/Fullscreen button - mobile optimized */}
          <button
            onClick={() => openLightbox(currentImageIndex)}
            className="absolute top-2 sm:top-4 left-2 sm:left-4 text-white p-1.5 sm:p-2 rounded-full z-20 shadow-md transition-all duration-300 hover:scale-110 active:scale-95"
            style={{ backgroundColor: `${themeColors.primary}CC` }}
            aria-label="View in fullscreen"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>

          <Slider ref={sliderRef} {...mainSliderSettings}>
            {images.map((image, index) => (
              <div key={index} className="w-full focus:outline-none">
                <div 
                  className="cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    className="h-full w-full max-h-[50vh] sm:max-h-[60vh] md:max-h-[65vh] lg:max-h-[70vh] xl:max-h-[75vh] 
                               min-h-[200px] sm:min-h-[250px] md:min-h-[300px] object-cover rounded-xl md:rounded-2xl shadow-lg border border-gray-200/50"
                    loading={index === 0 ? "eager" : "lazy"}
                    src={image}
                    alt={`Property image ${index + 1} of ${images.length}`}
                    onLoad={() => handleMainImageLoad(index)}
                    onError={(e) => {
                      e.target.src = placeholderImage;
                      handleMainImageLoad(index);
                    }}
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Mobile-optimized Thumbnails Section */}
        {images.length > 1 && (
          <div className="w-full mt-3 sm:mt-4 md:mt-6">
            {/* Mobile: Horizontal scroll thumbnails */}
            <div className="block md:hidden">
              <div className="flex overflow-x-auto space-x-2 pb-2 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {images.map((image, index) => (
                  <div
                    key={index}
                    ref={(el) => (thumbnailRefs.current[index] = el)}
                    className="flex-shrink-0 cursor-pointer"
                    style={{ width: '80px' }} // Fixed width for mobile
                    onClick={() => changeImage(index)}
                  >
                    <div className="relative overflow-hidden rounded-lg">
                      <img
                        loading="lazy"
                        src={thumbnailLoaded[index] ? image : placeholderImage}
                        alt={`Thumbnail ${index + 1}`}
                        className={`w-full h-14 object-cover transition-all duration-300 border-2
                                   ${index === currentImageIndex 
                                     ? "opacity-100 scale-105 shadow-md" 
                                     : "border-gray-200/50 opacity-70"
                                   }`}
                        style={index === currentImageIndex ? { borderColor: themeColors.primary } : {}}
                        onLoad={() => handleThumbnailLoad(index)}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                      
                      {/* Active indicator overlay */}
                      {index === currentImageIndex && (
                        <div 
                          className="absolute inset-0 pointer-events-none rounded-lg"
                          style={{ backgroundColor: `${themeColors.primary}20` }}
                        ></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop: Grid thumbnails */}
            <div className="hidden md:block">
              <div
                className="grid gap-2 lg:gap-3 thumbnails pb-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(visibleThumbnails, images.length)}, 1fr)`
                }}
              >
                {images.slice(0, visibleThumbnails).map((image, index) => (
                  <div
                    key={index}
                    ref={(el) => (thumbnailRefs.current[index] = el)}
                    className="cursor-pointer group/thumb"
                    onClick={() => changeImage(index)}
                  >
                    <div className="relative overflow-hidden rounded-lg">
                      <img
                        loading="lazy"
                        src={thumbnailLoaded[index] ? image : placeholderImage}
                        alt={`Thumbnail ${index + 1}`}
                        className={`w-full h-16 lg:h-20 object-cover transition-all duration-300 border-2
                                   ${index === currentImageIndex 
                                     ? "opacity-100 scale-105 shadow-md" 
                                     : "border-gray-200/50 opacity-70 hover:opacity-100 hover:scale-105"
                                   }`}
                        style={index === currentImageIndex ? { borderColor: themeColors.primary } : {}}
                        onLoad={() => handleThumbnailLoad(index)}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                      
                      {/* Active indicator overlay */}
                      {index === currentImageIndex && (
                        <div 
                          className="absolute inset-0 pointer-events-none rounded-lg"
                          style={{ backgroundColor: `${themeColors.primary}20` }}
                        ></div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Show remaining images indicator if there are more (desktop only) */}
                {images.length > visibleThumbnails && (
                  <div className="cursor-pointer group/thumb relative" onClick={() => openLightbox(visibleThumbnails)}>
                    <div className="relative overflow-hidden rounded-lg">
                      <img
                        loading="lazy"
                        src={thumbnailLoaded[visibleThumbnails] ? images[visibleThumbnails] : placeholderImage}
                        alt={`View ${images.length - visibleThumbnails} more images`}
                        className="w-full h-16 lg:h-20 object-cover transition-all duration-300 border-2 border-gray-200/50 opacity-70 hover:opacity-100 hover:scale-105"
                        onLoad={() => handleThumbnailLoad(visibleThumbnails)}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                      
                      {/* Overlay showing remaining count */}
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded-lg">
                        <span className="text-white font-semibold text-sm lg:text-base">
                          +{images.length - visibleThumbnails}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Video Section - mobile optimized */}
        {video && (
          <div className="w-full mt-4 sm:mt-6 md:mt-8">
            <div className="video-responsive w-full rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-gray-200/50">
              <div
                dangerouslySetInnerHTML={{ __html: video }}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile-optimized Lightbox Modal */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
          {/* Close button - mobile optimized */}
          <button
            onClick={closeLightbox}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white p-2 sm:p-3 rounded-full z-60 transition-all duration-300 hover:scale-110 active:scale-95"
            style={{ backgroundColor: `${themeColors.primary}CC` }}
            aria-label="Close fullscreen view"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter in lightbox - mobile optimized */}
          <div 
            className="absolute top-2 sm:top-4 left-2 sm:left-4 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm z-60"
            style={{ backgroundColor: `${themeColors.primary}CC` }}
          >
            {lightboxIndex + 1} / {images.length}
          </div>

          {/* Navigation arrows in lightbox - mobile optimized */}
          {images.length > 1 && (
            <>
              <button
                className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 text-white p-2 sm:p-4 rounded-full 
                           transition-all duration-300 hover:scale-110 active:scale-95 z-60"
                style={{ backgroundColor: `${themeColors.primary}CC` }}
                onClick={() => lightboxSliderRef.current?.slickPrev()}
                aria-label="Previous image"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 text-white p-2 sm:p-4 rounded-full 
                           transition-all duration-300 hover:scale-110 active:scale-95 z-60"
                style={{ backgroundColor: `${themeColors.primary}CC` }}
                onClick={() => lightboxSliderRef.current?.slickNext()}
                aria-label="Next image"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Lightbox image slider - mobile optimized */}
          <div className="w-full h-full max-w-6xl max-h-[90vh] mx-2 sm:mx-4">
            <Slider ref={lightboxSliderRef} {...lightboxSliderSettings}>
              {images.map((image, index) => (
                <div key={index} className="w-full h-full flex items-center justify-center">
                  <img
                    className="max-w-full max-h-[90vh] object-contain"
                    src={image}
                    alt={`Property image ${index + 1} in fullscreen`}
                    onError={(e) => {
                      e.target.src = placeholderImage;
                    }}
                  />
                </div>
              ))}
            </Slider>
          </div>

          {/* Lightbox thumbnails - hidden on mobile, visible on desktop */}
          {images.length > 1 && (
            <div className="hidden sm:flex absolute bottom-4 left-1/2 transform -translate-x-1/2 space-x-2 max-w-md overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => lightboxSliderRef.current?.slickGoTo(index)}
                  className={`flex-shrink-0 w-12 h-9 sm:w-16 sm:h-12 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                    index === lightboxIndex 
                      ? 'opacity-100 scale-105' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={index === lightboxIndex ? { borderColor: themeColors.primary } : { borderColor: 'transparent' }}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Gallery;